import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, LogIn, Download, Share as ShareIcon, Plus, X } from 'lucide-react';

const LOGO_URL = "/vprep-logo.png";
const HERO_IMAGE = "https://images.unsplash.com/photo-1690951784638-1f9039d74a6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjB0aWZmaW4lMjBsdW5jaCUyMGJveCUyMGZvb2R8ZW58MHx8fHwxNzcxMjgzMzMwfDA&ixlib=rb-4.1.0&q=85";
const MOBILE_BG = "https://customer-assets.emergentagent.com/job_d905a835-07e7-4363-a5a5-1be35c4871aa/artifacts/l3fxdxhn_IMG_8980%202.JPG";

export const Login = () => {
  // Prefill admin credentials ONLY on the preview (dev) environment
  const isPreview = window.location.hostname.endsWith('preview.emergentagent.com');
  const [username, setUsername] = useState(isPreview ? 'admin' : '');
  const [password, setPassword] = useState(isPreview ? 'admin123' : '');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deferredInstall, setDeferredInstall] = useState(null);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const ua = window.navigator.userAgent;
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;

  // Capture Chrome/Android install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredInstall(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredInstall) {
      deferredInstall.prompt();
      try {
        await deferredInstall.userChoice;
      } catch (e) {
        // ignore
      }
      setDeferredInstall(null);
      return;
    }
    // No native prompt available -> show manual instructions
    setShowInstallHelp(true);
  };

  // Load saved username on mount (passwords are never stored)
  useEffect(() => {
    const savedUsername = localStorage.getItem('virsa_remember_user');
    // Clean up legacy insecure credential storage
    localStorage.removeItem('virsa_remember');
    localStorage.removeItem('virsa_token');
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(username, password);
      
      // Remember only the username, never the password
      if (rememberMe) {
        localStorage.setItem('virsa_remember_user', username);
      } else {
        localStorage.removeItem('virsa_remember_user');
      }
      
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Form Section - with blurred tiffin background on mobile */}
      <div 
        className="flex-1 flex items-start md:items-center justify-center p-4 md:p-8 pt-8 md:pt-0 relative"
        style={{ backgroundColor: '#124242' }}
      >
        {/* Mobile blurred background */}
        <div 
          className="absolute inset-0 lg:hidden bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${MOBILE_BG})`,
            filter: 'blur(4px)',
            transform: 'scale(1.05)'
          }}
        />
        {/* Dark overlay for mobile */}
        <div className="absolute inset-0 lg:hidden bg-[#124242]/70" />
        
        {/* Desktop gradient background */}
        <div className="absolute inset-0 hidden lg:block bg-gradient-to-br from-[#124242] to-[#0d3232]" />
        
        <div className="w-full max-w-sm md:max-w-md relative z-10">
          {/* Logo and branding - smaller on mobile */}
          <div className="text-center mb-4 md:mb-8">
            <img src={LOGO_URL} alt="VPrep" className="h-16 md:h-24 mx-auto mb-2 md:mb-4" />
            <h1 className="text-2xl md:text-3xl font-bold text-[#fefefe] mb-1">
              Tiffin Management
            </h1>
            <p className="text-[#faa023] text-sm md:text-base">Taste of Punjab</p>
          </div>

          {/* Sign In Card - compact padding on mobile */}
          <div className="bg-[#fefefe] rounded-xl md:rounded-2xl p-5 md:p-8 shadow-2xl">
            <h2 className="text-xl md:text-2xl font-bold text-stone-800 mb-4 md:mb-6 text-center">
              Sign In
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1.5">
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="virsa-input h-11"
                  placeholder="Enter your username"
                  data-testid="login-username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="virsa-input h-11 pr-12"
                    placeholder="Enter your password"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Remember Me Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-stone-300 text-[#124242] focus:ring-[#124242]"
                  data-testid="remember-me-checkbox"
                />
                <label htmlFor="remember-me" className="text-sm text-stone-600 select-none cursor-pointer">
                  Remember me
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary h-11 text-base md:text-lg"
                data-testid="login-submit"
              >
                {loading ? (
                  <span className="spinner" />
                ) : (
                  <>
                    <LogIn size={20} />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-4 text-center">
              <p className="text-stone-400 text-xs md:text-sm">
                Contact admin for account access
              </p>
            </div>

            {!isStandalone && (
              <div className="mt-4 pt-4 border-t border-stone-100">
                <button
                  type="button"
                  onClick={handleInstallClick}
                  className="w-full flex items-center justify-center gap-2 h-11 rounded-lg border-2 border-[#124242] text-[#124242] font-semibold text-sm md:text-base hover:bg-[#124242] hover:text-white transition-colors"
                  data-testid="install-app-btn"
                >
                  <Download size={18} />
                  <span>Install as App on your phone</span>
                </button>
                <p className="text-center text-stone-400 text-[11px] md:text-xs mt-2">
                  {deferredInstall ? 'One tap to add to your home screen' : 'Tap for install instructions'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Install instructions modal */}
      {showInstallHelp && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-end md:items-center justify-center p-4"
          onClick={() => setShowInstallHelp(false)}
          data-testid="ios-install-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowInstallHelp(false)}
              className="absolute top-3 right-3 text-stone-400 hover:text-stone-600"
              aria-label="Close"
              data-testid="ios-install-close"
            >
              <X size={20} />
            </button>
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#124242]/10 mb-3">
                <Download size={22} className="text-[#124242]" />
              </div>
              <h3 className="text-lg font-bold text-stone-800">
                {isIos ? 'Install on iPhone' : isAndroid ? 'Install on Android' : 'Install on this device'}
              </h3>
              <p className="text-sm text-stone-500 mt-1">Add VPrep to your Home Screen</p>
            </div>
            {isIos ? (
              <ol className="space-y-3 text-sm text-stone-700">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#faa023] text-white text-xs font-bold flex items-center justify-center">1</span>
                  <span className="flex-1 pt-0.5">
                    Open this page in <span className="font-semibold">Safari</span> (not Chrome).
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#faa023] text-white text-xs font-bold flex items-center justify-center">2</span>
                  <span className="flex-1 pt-0.5">
                    Tap the <span className="inline-flex items-center gap-1 font-semibold"><ShareIcon size={14} /> Share</span> button in the toolbar.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#faa023] text-white text-xs font-bold flex items-center justify-center">3</span>
                  <span className="flex-1 pt-0.5">
                    Scroll and tap <span className="inline-flex items-center gap-1 font-semibold"><Plus size={14} /> Add to Home Screen</span>, then <span className="font-semibold">Add</span>.
                  </span>
                </li>
              </ol>
            ) : isAndroid ? (
              <ol className="space-y-3 text-sm text-stone-700">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#faa023] text-white text-xs font-bold flex items-center justify-center">1</span>
                  <span className="flex-1 pt-0.5">
                    Open this page in <span className="font-semibold">Chrome</span>.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#faa023] text-white text-xs font-bold flex items-center justify-center">2</span>
                  <span className="flex-1 pt-0.5">
                    Tap the <span className="font-semibold">⋮ menu</span> at the top right.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#faa023] text-white text-xs font-bold flex items-center justify-center">3</span>
                  <span className="flex-1 pt-0.5">
                    Tap <span className="font-semibold">Install app</span> or <span className="font-semibold">Add to Home screen</span>.
                  </span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-stone-700">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#faa023] text-white text-xs font-bold flex items-center justify-center">1</span>
                  <span className="flex-1 pt-0.5">
                    Open this page on your <span className="font-semibold">phone</span> for the best install experience.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#faa023] text-white text-xs font-bold flex items-center justify-center">2</span>
                  <span className="flex-1 pt-0.5">
                    In Chrome, look for the <span className="font-semibold">Install</span> icon in the address bar, or open the <span className="font-semibold">⋮ menu</span> → <span className="font-semibold">Install VPrep</span>.
                  </span>
                </li>
              </ol>
            )}
            <button
              type="button"
              onClick={() => setShowInstallHelp(false)}
              className="w-full mt-6 h-11 rounded-lg bg-[#124242] text-white font-semibold text-sm hover:bg-[#0d3232] transition-colors"
              data-testid="ios-install-done"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Image Section - Desktop only */}
      <div
        className="hidden lg:block flex-1 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      >
        <div className="h-full bg-black/30 flex items-end p-12">
          <div className="text-[#fefefe]">
            <h2 className="text-4xl font-bold mb-2">
              Authentic Punjabi Flavors
            </h2>
            <p className="text-lg opacity-90">
              Manage your tiffin service with ease - recipes, rotations, and production all in one place.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
